import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/noticias")({
  
  head: () => ({ meta: [{ title: "Admin — Noticias | Mine Class" }, { name: "description", content: "Panel de administración de Noticias — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: NoticiasAdmin,
});

type PressLink = { title: string; url: string; image_url?: string };

type Noticia = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  category: string | null;
  is_published: boolean;
  published_at: string | null;
  sort_order: number;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  press_links: PressLink[] | null;
  media_links: PressLink[] | null;
  created_at: string;
};

const empty: Partial<Noticia> = {
  slug: "",
  title: "",
  excerpt: "",
  body_md: "",
  category: "",
  is_published: false,
  seo_title: "",
  seo_description: "",
  og_image_url: "",
  press_links: [],
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

async function uploadToMedia(file: File): Promise<string> {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  const path = `${userId}/noticias/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
  if (error) throw error;
  const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
  return data?.signedUrl ?? "";
}

function NoticiasAdmin() {
  const [items, setItems] = useState<Noticia[]>([]);
  const [editing, setEditing] = useState<Partial<Noticia> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [inlineImgBusy, setInlineImgBusy] = useState(false);
  const coverRef = useRef<HTMLInputElement>(null);
  const inlineImgRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  async function onCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setImgBusy(true);
    try {
      const url = await uploadToMedia(file);
      setEditing({ ...editing, og_image_url: url });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setImgBusy(false);
      if (coverRef.current) coverRef.current.value = "";
    }
  }

  function applyToSelection(fn: (sel: string) => { text: string; caretOffset?: number }) {
    const ta = bodyRef.current;
    const cur = editing?.body_md ?? "";
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = cur.slice(start, end);
    const { text, caretOffset } = fn(sel);
    const next = cur.slice(0, start) + text + cur.slice(end);
    setEditing({ ...editing!, body_md: next });
    setTimeout(() => {
      ta.focus();
      const pos = start + (caretOffset ?? text.length);
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  }

  function insertAtCursor(text: string) {
    applyToSelection(() => ({ text }));
  }

  function wrap(before: string, after = before, placeholder = "texto") {
    applyToSelection((sel) => {
      const inner = sel || placeholder;
      return { text: `${before}${inner}${after}`, caretOffset: before.length + inner.length };
    });
  }

  function prefixLines(prefix: string | ((i: number) => string), placeholder = "elemento") {
    applyToSelection((sel) => {
      const lines = (sel || placeholder).split("\n");
      const out = lines.map((l, i) => (typeof prefix === "function" ? prefix(i) : prefix) + l).join("\n");
      return { text: out };
    });
  }

  function setHeading(level: number) {
    if (level === 0) {
      applyToSelection((sel) => ({ text: (sel || "Párrafo").replace(/^#+\s*/, "") }));
      return;
    }
    const hashes = "#".repeat(level);
    applyToSelection((sel) => ({ text: `${hashes} ${(sel || "Título").replace(/^#+\s*/, "")}` }));
  }

  function insertLink() {
    const url = window.prompt("URL del enlace (https://…)");
    if (!url) return;
    applyToSelection((sel) => {
      const label = sel || "texto del enlace";
      return { text: `[${label}](${url.trim()})` };
    });
  }

  function insertColor() {
    const color = window.prompt("Color en HEX (ej: #7C3AED) o nombre CSS", "#7C3AED");
    if (!color) return;
    wrap(`<span style="color:${color.trim()}">`, `</span>`);
  }

  function insertPullquote() {
    applyToSelection((sel) => {
      const inner = sel || "Cita destacada";
      return { text: `\n\n<aside class="pullquote">${inner}</aside>\n\n` };
    });
  }

  function insertAlign(dir: "left" | "center" | "right") {
    applyToSelection((sel) => {
      const inner = sel || "texto";
      return { text: `\n\n<div style="text-align:${dir}">\n\n${inner}\n\n</div>\n\n` };
    });
  }

  async function onInlineImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setInlineImgBusy(true);
    try {
      const url = await uploadToMedia(file);
      insertAtCursor(`\n\n![${file.name}](${url})\n\n`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setInlineImgBusy(false);
      if (inlineImgRef.current) inlineImgRef.current.value = "";
    }
  }

  function insertEmbed() {
    const url = window.prompt("Pega la URL (YouTube, Spotify, LinkedIn, Vimeo, X, etc.)");
    if (!url) return;
    insertAtCursor(`\n\n::embed[${url.trim()}]::\n\n`);
  }


  async function load() {
    const { data } = await supabase
      .from("noticias")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    setItems((data ?? []) as Noticia[]);
  }
  useEffect(() => { load(); }, []);

  async function moveNoticia(n: Noticia, dir: -1 | 1) {
    const sorted = [...items];
    const idx = sorted.findIndex((x) => x.id === n.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    const updates = sorted.map((s, i) => ({ id: s.id, sort_order: i }));
    const a = updates.find((u) => u.id === n.id)!;
    const b = updates.find((u) => u.id === swap.id)!;
    [a.sort_order, b.sort_order] = [b.sort_order, a.sort_order];
    await Promise.all(updates.map((u) => supabase.from("noticias").update({ sort_order: u.sort_order }).eq("id", u.id)));
    load();
  }

  async function save() {
    if (!editing) return;
    setErr(null);
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const slug = editing.slug || slugify(editing.title ?? "");
      const payload = {
        slug,
        title: editing.title ?? "",
        excerpt: editing.excerpt || null,
        body_md: editing.body_md || null,
        category: editing.category || null,
        is_published: !!editing.is_published,
        published_at: editing.published_at
          ? editing.published_at
          : (editing.is_published ? new Date().toISOString() : null),
        seo_title: editing.seo_title || null,
        seo_description: editing.seo_description || null,
        og_image_url: editing.og_image_url || null,
        press_links: (editing.press_links ?? []) as unknown as import("@/integrations/supabase/types").Json,
        author_id: userId,
      };
      const q = editing.id
        ? supabase.from("noticias").update(payload).eq("id", editing.id)
        : supabase.from("noticias").insert(payload);
      const { error } = await q;
      if (error) throw error;
      await supabase.from("admin_audit_log").insert({
        actor_id: userId,
        action: editing.id ? "update_noticia" : "create_noticia",
        target_type: "noticia",
        target_id: editing.id ?? null,
        metadata: { slug },
      });
      setEditing(null);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(n: Noticia) {
    if (!confirm(`Eliminar noticia "${n.title}"?`)) return;
    await supabase.from("noticias").delete().eq("id", n.id);
    await supabase.from("admin_audit_log").insert({
      actor_id: (await supabase.auth.getUser()).data.user?.id,
      action: "delete_noticia",
      target_type: "noticia",
      target_id: n.id,
    });
    load();
  }

  function updatePress(idx: number, patch: Partial<PressLink>) {
    const list = [...(editing?.press_links ?? [])];
    list[idx] = { ...list[idx], ...patch };
    setEditing({ ...editing!, press_links: list });
  }
  function addPress() {
    setEditing({ ...editing!, press_links: [...(editing?.press_links ?? []), { title: "", url: "", image_url: "" }] });
  }
  function removePress(idx: number) {
    const list = [...(editing?.press_links ?? [])];
    list.splice(idx, 1);
    setEditing({ ...editing!, press_links: list });
  }
  async function uploadPressImage(idx: number, file: File) {
    try {
      const url = await uploadToMedia(file);
      updatePress(idx, { image_url: url });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    }
  }

  if (editing) {
    const press = editing.press_links ?? [];
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nueva"} noticia</h2>
        <Field label="Título">
          <input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>
        <Field label="Slug (URL)">
          <input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-sm" />
        </Field>
        <Field label="Categoría">
          <input value={editing.category ?? ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>
        <Field label="Resumen (excerpt)">
          <textarea value={editing.excerpt ?? ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>
        <Field label="Cuerpo">
          <div className="border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)]">
            <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-[var(--color-outline-variant)] bg-white text-sm">
              <select
                onChange={(e) => { setHeading(Number(e.target.value)); e.currentTarget.selectedIndex = 0; }}
                className="text-xs border border-[var(--color-outline-variant)] px-2 py-1 bg-white mr-1"
                defaultValue=""
                title="Estilo de párrafo"
              >
                <option value="" disabled>Párrafo ▾</option>
                <option value="0">Párrafo</option>
                <option value="2">Título H2</option>
                <option value="3">Título H3</option>
                <option value="4">Título H4</option>
              </select>
              <TbBtn onClick={() => wrap("**")} title="Negrita (Ctrl+B)"><b>B</b></TbBtn>
              <TbBtn onClick={() => wrap("*")} title="Cursiva (Ctrl+I)"><i>I</i></TbBtn>
              <TbBtn onClick={() => wrap("<u>", "</u>")} title="Subrayado"><u>U</u></TbBtn>
              <TbBtn onClick={() => wrap("~~")} title="Tachado"><s>S</s></TbBtn>
              <TbSep />
              <TbBtn onClick={insertColor} title="Color de texto"><span style={{ color: "#7C3AED" }}>A</span></TbBtn>
              <TbBtn onClick={() => wrap("<mark>", "</mark>")} title="Resaltar"><mark className="px-0.5">H</mark></TbBtn>
              <TbSep />
              <TbBtn onClick={insertLink} title="Enlace">🔗</TbBtn>
              <TbBtn onClick={() => inlineImgRef.current?.click()} disabled={inlineImgBusy} title="Imagen">
                {inlineImgBusy ? "…" : "🖼"}
              </TbBtn>
              <input ref={inlineImgRef} type="file" accept="image/*" onChange={onInlineImageUpload} className="hidden" />
              <TbBtn onClick={insertEmbed} title="Embed (YouTube, Spotify…)">▶</TbBtn>
              <TbSep />
              <TbBtn onClick={() => prefixLines("- ")} title="Lista con viñetas">• ≡</TbBtn>
              <TbBtn onClick={() => prefixLines((i) => `${i + 1}. `)} title="Lista numerada">1. ≡</TbBtn>
              <TbBtn onClick={() => prefixLines("> ")} title="Cita">❝</TbBtn>
              <TbBtn onClick={insertPullquote} title="Cuña / cita destacada (pullquote)">❞❞</TbBtn>
              <TbSep />
              <TbBtn onClick={() => insertAlign("left")} title="Alinear a la izquierda">⇤</TbBtn>
              <TbBtn onClick={() => insertAlign("center")} title="Centrar">≡</TbBtn>
              <TbBtn onClick={() => insertAlign("right")} title="Alinear a la derecha">⇥</TbBtn>
              <TbSep />
              <TbBtn onClick={() => insertAtCursor("\n\n---\n\n")} title="Línea horizontal">―</TbBtn>
            </div>
            <textarea
              ref={bodyRef}
              value={editing.body_md ?? ""}
              onChange={(e) => setEditing({ ...editing, body_md: e.target.value })}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**"); }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*"); }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); insertLink(); }
              }}
              rows={16}
              className="w-full px-3 py-2 font-mono text-sm bg-white border-0 focus:outline-none focus:ring-0 resize-y"
            />
          </div>
          <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
            Selecciona texto y aplica formato con la barra. Atajos: <kbd>Ctrl+B</kbd> negrita, <kbd>Ctrl+I</kbd> cursiva, <kbd>Ctrl+K</kbd> enlace.
            La cuña se muestra como cita destacada en la noticia.
          </p>
        </Field>


        <div className="grid md:grid-cols-2 gap-4">
          <Field label="SEO title">
            <input value={editing.seo_title ?? ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
          </Field>
          <Field label="Imagen de portada (1200×630 · 16:9)">
            <div className="space-y-2">
              {editing.og_image_url && (
                <div className="aspect-[1200/630] bg-[var(--color-surface-container-low)] overflow-hidden max-w-sm">
                  <img src={editing.og_image_url} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <input ref={coverRef} type="file" accept="image/*" onChange={onCoverUpload} disabled={imgBusy} className="text-sm" />
              {imgBusy && <p className="text-xs">Subiendo…</p>}
              <input value={editing.og_image_url ?? ""} onChange={(e) => setEditing({ ...editing, og_image_url: e.target.value })}
                placeholder="…o pega una URL" className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs font-mono" />
            </div>
          </Field>
        </div>
        <Field label="SEO description">
          <textarea value={editing.seo_description ?? ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} rows={2}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>

        <div className="border-t border-[var(--color-outline-variant)] pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm uppercase tracking-widest text-[var(--color-brand-purple)]">Mine Class en la prensa</h3>
            <button type="button" onClick={addPress} className="text-xs px-3 py-1.5 bg-[var(--color-brand-purple)] text-white">+ Agregar link</button>
          </div>
          <p className="text-xs text-[var(--color-on-surface-variant)] mb-3">Links de prensa asociados a esta noticia. Se mostrarán como carrusel bajo el artículo.</p>
          <div className="space-y-3">
            {press.map((p, i) => (
              <div key={i} className="border border-[var(--color-outline-variant)] p-3 grid md:grid-cols-[80px_1fr_auto] gap-3 items-start">
                <div>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-20 h-20 object-cover" />
                  ) : (
                    <div className="w-20 h-20 bg-[var(--color-surface-container-low)] flex items-center justify-center text-xs text-[var(--color-on-surface-variant)]">Sin img</div>
                  )}
                  <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadPressImage(i, e.target.files[0])} className="mt-1 text-[10px] w-20" />
                </div>
                <div className="space-y-2">
                  <input value={p.title} onChange={(e) => updatePress(i, { title: e.target.value })} placeholder="Título" className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm" />
                  <input value={p.url} onChange={(e) => updatePress(i, { url: e.target.value })} placeholder="URL https://…" className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-xs font-mono" />
                  <input value={p.image_url ?? ""} onChange={(e) => updatePress(i, { image_url: e.target.value })} placeholder="URL imagen (opcional)" className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-xs font-mono" />
                </div>
                <button type="button" onClick={() => removePress(i)} className="text-xs text-red-600">Eliminar</button>
              </div>
            ))}
            {press.length === 0 && <p className="text-xs text-[var(--color-on-surface-variant)]">Sin links de prensa.</p>}
          </div>
        </div>

        <Field label="Fecha de publicación (visible en el sitio)">
          <input
            type="datetime-local"
            value={editing.published_at ? new Date(editing.published_at).toISOString().slice(0, 16) : ""}
            onChange={(e) =>
              setEditing({
                ...editing,
                published_at: e.target.value ? new Date(e.target.value).toISOString() : null,
              })
            }
            className="border border-[var(--color-outline-variant)] px-3 py-2 text-sm"
          />
          <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
            Si dejas vacío y publicas, se usará la fecha actual.
          </p>
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} />
          Publicar
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold disabled:opacity-50">
            {busy ? "Guardando..." : "Guardar"}
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-lg">Noticias ({items.length})</h2>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
          + Nueva noticia
        </button>
      </div>
      <div className="bg-white border border-[var(--color-outline-variant)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface-container-low)] text-left">
            <tr>
              <th className="px-4 py-3 w-16">Orden</th>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((n, i) => (
              <tr key={n.id} className="border-t border-[var(--color-outline-variant)]">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveNoticia(n, -1)}
                      disabled={i === 0}
                      className="text-xs disabled:opacity-30 hover:text-[var(--color-brand-purple)]"
                      title="Subir"
                    >▲</button>
                    <button
                      onClick={() => moveNoticia(n, 1)}
                      disabled={i === items.length - 1}
                      className="text-xs disabled:opacity-30 hover:text-[var(--color-brand-purple)]"
                      title="Bajar"
                    >▼</button>
                  </div>
                </td>
                <td className="px-4 py-3 font-semibold">{n.title}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {n.is_published ? (
                    <Link to="/comunicaciones/noticias/$slug" params={{ slug: n.slug }} className="underline">/{n.slug}</Link>
                  ) : (`/${n.slug}`)}
                </td>
                <td className="px-4 py-3">
                  {n.is_published ? <span className="text-green-700 text-xs font-bold">PUBLICADA</span> : <span className="text-[var(--color-on-surface-variant)] text-xs">Borrador</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {n.published_at ? new Date(n.published_at).toLocaleDateString("es-CL") : <span className="text-[var(--color-on-surface-variant)]">—</span>}
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button onClick={() => setEditing(n)} className="text-xs underline">Editar</button>
                  <button onClick={() => remove(n)} className="text-xs text-red-600">Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">Sin noticias.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}

function TbBtn({ onClick, disabled, title, children }: { onClick: () => void; disabled?: boolean; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="min-w-[30px] h-[30px] px-2 text-sm border border-transparent hover:border-[var(--color-outline-variant)] hover:bg-[var(--color-surface-container-low)] disabled:opacity-40 flex items-center justify-center leading-none"
    >
      {children}
    </button>
  );
}

function TbSep() {
  return <span className="mx-1 w-px h-5 bg-[var(--color-outline-variant)]" aria-hidden />;
}

